import React, { useState } from 'react';
import { Card } from 'primereact/card';
import { Badge } from 'primereact/badge';
import { Button } from 'primereact/button';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { InputText } from 'primereact/inputtext';
import { Divider } from 'primereact/divider';
import { useTheme } from '../hooks/useTheme';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';

export default function FaqPage() {
    const [activeIndex, setActiveIndex] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const { theme, toggleTheme } = useTheme();

    const faqData = [
        {
            id: 1,
            category: 'Pedidos y seguimiento',
            question: '¿Cómo hago seguimiento a mi pedido?',
            answer: 'Te enviaremos un enlace con el estado actualizado de tu compra, también tienes los módulos de seguimiento como "Sigue tu pedido" o tu panel de cliente para que puedas estar pendiente.',
            icon: 'pi pi-search',
            severity: 'info',
            keywords: ['seguimiento', 'pedido', 'estado', 'compra', 'panel', 'cliente']
        },
        {
            id: 2,
            category: 'Garantía y soporte',
            question: '¿Qué hago si mi laptop llega con fallas?',
            answer: 'Contáctanos inmediatamente, nuestro equipo gestionará la garantía por ti. Tenemos un proceso rápido y eficiente para resolver cualquier problema con tu producto.',
            icon: 'pi pi-wrench',
            severity: 'warning',
            keywords: ['laptop', 'fallas', 'garantía', 'contacto', 'soporte', 'problema']
        },
        {
            id: 3,
            category: 'Facturación',
            question: '¿Puedo pedir factura o boleta?',
            answer: 'Sí, al momento de pagar puedes seleccionar el tipo de comprobante que necesitas. Ofrecemos tanto facturas como boletas según tus requerimientos.',
            icon: 'pi pi-file',
            severity: 'success',
            keywords: ['factura', 'boleta', 'comprobante', 'pago', 'seleccionar']
        },
        {
            id: 4,
            category: 'Métodos de pago',
            question: '¿Puedo pagar a plazos?',
            answer: 'Ofrecemos cuotas con tarjetas participantes; consulta las condiciones antes de pagar, ten presente que las condiciones de pago están sujetas a las políticas de cada canal de ventas de Laptops.pe',
            icon: 'pi pi-credit-card',
            severity: 'help',
            keywords: ['plazos', 'cuotas', 'tarjetas', 'pago', 'condiciones', 'canal']
        }
    ];

    const additionalFaqs = [
        {
            id: 5,
            category: 'Envíos',
            question: '¿Cuánto tiempo demora la entrega?',
            answer: 'Los tiempos de entrega varían según tu ubicación: Lima Metropolitana 1-3 días, ciudades principales 2-5 días, zonas rurales 5-10 días hábiles.',
            icon: 'pi pi-truck',
            severity: 'info',
            keywords: ['entrega', 'tiempo', 'envío', 'lima', 'días', 'ubicación']
        },
        {
            id: 6,
            category: 'Productos',
            question: '¿Los productos son nuevos?',
            answer: 'Sí, todos nuestros productos son completamente nuevos y cuentan con garantía oficial del fabricante.',
            icon: 'pi pi-verified',
            severity: 'success',
            keywords: ['productos', 'nuevos', 'garantía', 'fabricante', 'oficial']
        },
        {
            id: 7,
            category: 'Cambios y devoluciones',
            question: '¿Puedo cambiar mi producto?',
            answer: 'Sí, tienes hasta 7 días calendario para solicitar un cambio. El producto debe estar sin uso y con empaques originales.',
            icon: 'pi pi-refresh',
            severity: 'warning',
            keywords: ['cambio', 'producto', 'días', 'empaques', 'originales']
        },
        {
            id: 8,
            category: 'Soporte técnico',
            question: '¿Ofrecen soporte técnico?',
            answer: 'Sí, contamos con un equipo de soporte técnico especializado disponible para ayudarte antes y después de tu compra.',
            icon: 'pi pi-headphones',
            severity: 'help',
            keywords: ['soporte', 'técnico', 'equipo', 'especializado', 'ayuda']
        }
    ];

    const allFaqs = [...faqData, ...additionalFaqs];

    const filteredFaqs = allFaqs.filter(faq => 
        faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
        faq.keywords.some(keyword => keyword.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const categories = [...new Set(allFaqs.map(faq => faq.category))];

    const quickActions = [
        {
            title: 'Seguir pedido',
            description: 'Rastrea tu compra en tiempo real',
            icon: 'pi pi-map-marker',
            action: 'tracking'
        },
        {
            title: 'Soporte técnico',
            description: 'Contacta a nuestros expertos',
            icon: 'pi pi-headphones',
            action: 'support'
        },
        {
            title: 'Garantía',
            description: 'Gestiona tu garantía',
            icon: 'pi pi-shield',
            action: 'warranty'
        },
        {
            title: 'Chat en vivo',
            description: 'Habla con un asesor',
            icon: 'pi pi-comments',
            action: 'chat'
        }
    ];

    return (
        <div className={'landing-page theme-' + theme}>
            <Header theme={theme} onToggleTheme={toggleTheme} />
            <div className="surface-ground min-h-screen">
            {/* Hero Section */}
            <div className="surface-section">
                <div className="container mx-4">
                    <div className="grid">
                        <div className="col-12">
                            <h1 className="text-4xl lg:text-6xl font-bold text-primary mb-4 line-height-2">
                                Resolvemos tus dudas rápidamente
                            </h1>
                            <p className="text-xl lg:text-2xl text-700 line-height-3 mb-6">
                                En esta sección encontrarás respuestas a las consultas más comunes. 
                                Si no encuentras lo que buscas, nuestro equipo está listo para ayudarte.
                            </p>
                            <div className="flex flex-wrap gap-3">
                                <Badge value="Respuestas rápidas" severity="success" size={"large"} />
                                <Badge value="Soporte 24/7" severity="info" size={"large"} />
                                <Badge value="Expertos disponibles" severity="warning" size={"large"} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-4 py-6">

                {/* Acciones rápidas */}
                <div className="mb-4">
                    <Card>
                        <div className="p-0 text-center">
                            <h2 className="text-3xl font-bold text-primary mb-6">Acciones rápidas</h2>
                            <div className="grid">
                                {quickActions.map((action, index) => (
                                    <div key={index} className="col-12 md:col-6 lg:col-3">
                                        <Card className="h-full transition-all transition-duration-300 hover:shadow-lg cursor-pointer">
                                            <div className="text-center p-4">
                                                <div className="inline-flex align-items-center justify-content-center w-4rem h-4rem bg-primary border-circle mb-4">
                                                    <i className={`${action.icon} text-3xl text-primary`}></i>
                                                </div>
                                                <h3 className="text-xl font-semibold text-900 mb-3">{action.title}</h3>
                                                <p className="text-600 line-height-3 mb-4">{action.description}</p>
                                                <Button label="Acceder" icon="pi pi-arrow-right" className="p-button-outlined" />
                                            </div>
                                        </Card>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Card>
                </div>
                {/* Buscador de preguntas */}
                <div className="mb-6">
                    <Card>
                        <div className="p-0 text-center">
                            <h2 className="text-3xl font-bold text-primary mb-4">¿Qué necesitas saber?</h2>
                            <p className="text-lg text-700 mb-4">Busca tu pregunta o navega por las categorías</p>
                            <div className="flex justify-content-center">
                                <div className="p-inputgroup w-full md:w-6">
                                    <span className="p-inputgroup-addon">
                                        <i className="pi pi-search"></i>
                                    </span>
                                    <InputText 
                                        placeholder="Escribe tu pregunta aquí..." 
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full"
                                    />
                                </div>
                            </div>
                            {searchTerm && (
                                <div className="mt-3">
                                    <Badge value={`${filteredFaqs.length} resultado(s) encontrado(s)`} severity="info" />
                                </div>
                            )}
                        </div>
                    </Card>
                </div>

                {/* Preguntas frecuentes principales */}
                <div className="mb-4">
                    <Card>
                        <div className="p-0 text-center">
                            <h2 className="text-3xl font-bold text-primary mb-6">Preguntas más frecuentes</h2>
                            <Accordion activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)} multiple>
                                {filteredFaqs.slice(0, 4).map((faq, index) => (
                                    <AccordionTab 
                                        key={faq.id}
                                        header={
                                            <div className="flex align-items-center gap-3">
                                                <div className="inline-flex align-items-center justify-content-center w-2rem h-2rem bg-primary border-circle">
                                                    <i className={`${faq.icon} text-primary`}></i>
                                                </div>
                                                <span className="font-semibold text-900">{faq.question}</span>
                                                <Badge value={faq.category} severity={faq.severity} className="ml-auto" />
                                            </div>
                                        }
                                    >
                                        <div className="p-2">
                                            <div className="surface-primary text-left border-round p-2">
                                                <p className="text-700 line-height-3 m-0">{faq.answer}</p>
                                            </div>
                                            <div className="mt-3 flex gap-2">
                                                <Button label="¿Te ayudó?" icon="pi pi-thumbs-up" className="p-button-outlined p-button-sm" />
                                                <Button label="Contactar soporte" icon="pi pi-headphones" className="p-button-outlined p-button-sm" />
                                            </div>
                                        </div>
                                    </AccordionTab>
                                ))}
                            </Accordion>
                        </div>
                    </Card>
                </div>

                {/* Todas las preguntas por categorías */}
                {filteredFaqs.length > 4 && (
                    <div className="mb-4">
                        <Card>
                            <div className="p-0 text-center">
                                <h2 className="text-3xl font-bold text-primary mb-4">Más preguntas frecuentes</h2>
                                <Accordion multiple>
                                    {filteredFaqs.slice(4).map((faq, index) => (
                                        <AccordionTab 
                                            key={faq.id}
                                            header={
                                                <div className="flex align-items-center gap-3">
                                                    <div className="inline-flex align-items-center justify-content-center w-2rem h-2rem bg-primary border-circle">
                                                        <i className={`${faq.icon} text-primary`}></i>
                                                    </div>
                                                    <span className="font-semibold text-primary">{faq.question}</span>
                                                    <Badge value={faq.category} severity={faq.severity} className="ml-auto" />
                                                </div>
                                            }
                                        >
                                            <div className="p-4">
                                                <div className="surface-100 border-round p-2 text-left">
                                                    <p className="text-700 line-height-3 m-0">{faq.answer}</p>
                                                </div>
                                                <div className="mt-3 flex gap-2">
                                                    <Button label="¿Te ayudó?" icon="pi pi-thumbs-up" className="p-button-outlined p-button-sm" />
                                                    <Button label="Contactar soporte" icon="pi pi-headphones" className="p-button-outlined p-button-sm" />
                                                </div>
                                            </div>
                                        </AccordionTab>
                                    ))}
                                </Accordion>
                            </div>
                        </Card>
                    </div>
                )}

                {/* Categorías de ayuda */}
                <div className="mb-4">
                    <Card>
                        <div className="p-0 text-center">
                            <h2 className="text-3xl font-bold text-primary mb-4">Explora por categorías</h2>
                            <div className="grid">
                                {categories.map((category, index) => {
                                    const categoryFaqs = allFaqs.filter(faq => faq.category === category);
                                    const categoryIcon = categoryFaqs[0]?.icon || 'pi pi-folder';
                                    return (
                                        <div key={index} className="col-12 md:col-6 lg:col-4">
                                            <div className="surface-100 border-round p-4 text-center hover:shadow-2 transition-all transition-duration-300 cursor-pointer">
                                                <i className={`${categoryIcon} text-6xl text-primary mb-3`}></i>
                                                <h4 className="text-lg font-semibold text-900 mb-2">{category}</h4>
                                                <Badge value={`${categoryFaqs.length} pregunta(s)`} severity="info" />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </Card>
                </div>

                {/* No se encontraron resultados */}
                {searchTerm && filteredFaqs.length === 0 && (
                    <div className="mb-6">
                        <Card>
                            <div className="p-6 text-center">
                                <i className="pi pi-search text-6xl text-400 mb-4"></i>
                                <h3 className="text-2xl font-bold text-900 mb-3">No encontramos resultados</h3>
                                <p className="text-lg text-700 mb-4">No hay preguntas que coincidan con tu búsqueda "{searchTerm}"</p>
                                <div className="flex justify-content-center gap-3">
                                    <Button label="Limpiar búsqueda" icon="pi pi-times" onClick={() => setSearchTerm('')} className="p-button-outlined" />
                                    <Button label="Contactar soporte" icon="pi pi-headphones" />
                                </div>
                            </div>
                        </Card>
                    </div>
                )}

                {/* Sección de contacto */}
                <div className="mb-4">
                    <Card>
                        <div className="p-0">
                            <div className="grid align-items-center">
                                <div className="col-12">
                                    <h2 className="text-3xl font-bold text-900 mb-4">¿No encontraste lo que buscabas?</h2>
                                    <p className="text-xl text-700 mb-4 line-height-3">
                                        Nuestro equipo de soporte está disponible para resolver cualquier duda específica que tengas.
                                    </p>
                                    <div className="flex flex-wrap gap-3">
                                        <Button label="Chat en vivo" icon="pi pi-comments" className="p-button-lg" />
                                        <Button label="Enviar consulta" icon="pi pi-envelope" className="p-button-outlined p-button-lg" />
                                        <Button label="Llamar ahora" icon="pi pi-phone" className="p-button-outlined p-button-lg" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Información adicional */}
                <div className="mb-6">
                    <Card>
                        <div className="p-0 text-center">
                            <h2 className="text-3xl font-bold text-primary mb-4">Horarios de atención</h2>
                            <div className="grid">
                                <div className="col-12 md:col-4">
                                    <div className="p-3 bg-blue-200 border-round border-left-3 border-blue-500">
                                        <h4 className="text-lg font-semibold text-blue-800 mb-2">Chat en línea</h4>
                                        <p className="text-blue-700 m-0">Lunes a Domingo<br />24 horas</p>
                                    </div>
                                </div>
                                <div className="col-12 md:col-4">
                                    <div className="p-3 bg-green-200 border-round border-left-3 border-green-500">
                                        <h4 className="text-lg font-semibold text-green-800 mb-2">Teléfono</h4>
                                        <p className="text-green-700 m-0">Lunes a Viernes<br />9:00 AM - 6:00 PM</p>
                                    </div>
                                </div>
                                <div className="col-12 md:col-4">
                                    <div className="p-3 bg-orange-200 border-round border-left-3 border-orange-500">
                                        <h4 className="text-lg font-semibold text-orange-800 mb-2">Email</h4>
                                        <p className="text-orange-700 m-0">Respuesta en<br />24 horas máximo</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
            <Footer />
        </div></div>
    );
}